import {
  AwaitContainer,
  Button,
  Item,
  ItemActions,
  ItemContent,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Spinner,
  Textarea,
  useInfiniteScroll,
  useReverseScroll,
} from '@ibsys/kit';
import { IconButton, Tooltip } from '@/shared/ui';
import { Check, Pencil, SendIcon, Trash2, X } from 'lucide-react';
import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { cn, scrollIntoViewWithinContainer } from '@/shared/lib';
import {
  CommentResponse,
  CONVERSATION_COMMENT_PARAM,
  useCreateConversationComment,
  useDeleteConversationComment,
  useInfiniteConversationComments,
  useUpdateConversationComment,
} from '@/entities/conversations';
import { useMyProfile } from '@/entities/profile';
import { formatHumanDate } from '@/shared/lib/utils/date';
import { getShortName } from '@/shared/lib';

type Props = { conversationId: number };

export const CommentsTab: FC<Props> = ({ conversationId }) => {
  const { t, i18n } = useTranslation(['dialogs', 'common']);
  const bottomRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const shouldRestoreRef = useRef(false);
  const { captureScrollHeight, restoreScrollPosition } = useReverseScroll(viewportRef);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteConversationComments(
    conversationId,
    { limit: 50 },
  );
  const { data: profile } = useMyProfile();
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const createMutation = useCreateConversationComment();
  const updateMutation = useUpdateConversationComment();
  const deleteMutation = useDeleteConversationComment();
  const comments: CommentResponse[] = useMemo(() => data?.comments.toReversed() ?? [], [data?.comments]);

  // Подсветка и автоскролл к найденному комментарию из поиска по комментариям диалогов
  // (spec: 03 IL/Поиск по комментариям в диалогах → Переход на экран диалога).
  const [searchParams] = useSearchParams();
  const matchedCommentIdParam = searchParams.get(CONVERSATION_COMMENT_PARAM);
  const matchedCommentId = matchedCommentIdParam != null ? Number(matchedCommentIdParam) : null;
  const isValidMatchedCommentId = matchedCommentId != null && !Number.isNaN(matchedCommentId);
  const commentRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const lastScrolledMatchRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isValidMatchedCommentId) return;
    if (isLoading) return;
    if (lastScrolledMatchRef.current === matchedCommentId) return;
    const target = commentRefs.current.get(matchedCommentId as number);
    if (!target) return;
    lastScrolledMatchRef.current = matchedCommentId as number;
    scrollIntoViewWithinContainer(target, { align: 'center', behavior: 'smooth' });
  }, [comments, isLoading, isValidMatchedCommentId, matchedCommentId]);

  const handleCreate = async () => {
    const text = newComment.trim();
    if (!text || createMutation.isPending) return;
    scrollIntoViewWithinContainer(bottomRef.current);
    await createMutation.mutate({ id: conversationId, payload: { text } }, { onSuccess: () => setNewComment('') });
    scrollIntoViewWithinContainer(bottomRef.current);
  };

  const handleStartEdit = (comment: CommentResponse) => {
    setEditingId(comment.id);
    setEditingText(comment.text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };

  const handleSaveEdit = (id: number) => {
    const text = editingText.trim();
    if (!text || updateMutation.isPending) return;
    updateMutation.mutate({ id, payload: { text } }, { onSuccess: handleCancelEdit });
  };

  const handleDelete = (id: number) => {
    if (deleteMutation.isPending) return;
    deleteMutation.mutate(id);
  };

  const canManageComment = (comment: CommentResponse) => profile && String(comment.user.id) === String(profile.id);

  const handlePress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.shiftKey && event.code === 'Enter') {
      event.preventDefault();
      handleCreate();
    }
  };

  const handleEditPress = (event: React.KeyboardEvent<HTMLTextAreaElement>, commentId: number) => {
    if (event.shiftKey && event.code === 'Enter') {
      event.preventDefault();
      handleSaveEdit(commentId);
    }
  };

  useEffect(() => {
    if (isValidMatchedCommentId) return;
    scrollIntoViewWithinContainer(bottomRef.current);
  }, [isValidMatchedCommentId]);

  useLayoutEffect(() => {
    if (shouldRestoreRef.current) {
      restoreScrollPosition();
      shouldRestoreRef.current = false;
    } else if (!isValidMatchedCommentId) {
      scrollIntoViewWithinContainer(bottomRef.current);
    }
  }, [comments, isValidMatchedCommentId]);

  const handleLoadMore = useCallback(() => {
    captureScrollHeight();
    shouldRestoreRef.current = true;
    fetchNextPage();
  }, [captureScrollHeight, fetchNextPage]);

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    threshold: 500,
    direction: 'reverse',
    root: viewportRef,
  });

  if (isLoading) return null;

  return (
    <AwaitContainer isLoading={isLoading} className="flex flex-col h-full overflow-hidden">
      {/* Список занимает только оставшуюся высоту: иначе он вытягивает раздел ниже видимой
          области, и поле ввода комментария оказывается за краем экрана */}
      <ScrollArea className="flex-1 min-h-0" viewPortProps={{ ref: viewportRef }}>
        <Sentinel ref={sentinelRef}>
          {isFetchingNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
        </Sentinel>
        <div className="flex flex-col flex-1 overflow-auto space-y-3 p-1 pt-4">
          {comments.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('dialogs:noData')}</p>
          ) : (
            comments.map((comment) => {
              const isOwn = canManageComment(comment);
              const isEditing = editingId === comment.id;
              const isMatched = isValidMatchedCommentId && comment.id === matchedCommentId;
              return (
                <div
                  key={comment.id}
                  ref={(node) => {
                    if (node) commentRefs.current.set(comment.id, node);
                    else commentRefs.current.delete(comment.id);
                  }}
                >
                  <Item variant="outline" size="sm" className={cn(isMatched && 'ring-2 ring-primary/50 bg-primary/5')}>
                    <ItemHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                      <div>
                        <ItemTitle className="text-sm font-medium">
                          {getShortName(comment.user.firstName, comment.user.lastName, comment.user.middleName)}
                        </ItemTitle>
                        <p className="text-xs text-muted-foreground">
                          {formatHumanDate(i18n, comment.createdAt, { withTime: true })}
                          {comment.isUpdated && ` · ${t('common:updated')}`}
                        </p>
                      </div>
                      {isOwn && (
                        <ItemActions className="flex gap-1">
                          {isEditing ? (
                            <>
                              <IconButton
                                size="icon"
                                variant="ghost"
                                icon={<Check className="w-4 h-4" />}
                                label={t('common:save')}
                                onClick={() => handleSaveEdit(comment.id)}
                                disabled={updateMutation.isPending || !editingText.trim()}
                              />
                              <IconButton
                                size="icon"
                                variant="ghost"
                                icon={<X className="w-4 h-4" />}
                                label={t('common:cancel')}
                                onClick={handleCancelEdit}
                              />
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="icon"
                                variant="ghost"
                                icon={<Pencil className="w-4 h-4" />}
                                label={t('common:edit')}
                                onClick={() => handleStartEdit(comment)}
                              />
                              <IconButton
                                size="icon"
                                variant="ghost"
                                color="destructive"
                                icon={<Trash2 className="w-4 h-4" />}
                                label={t('dialogs:deleteComment')}
                                onClick={() => handleDelete(comment.id)}
                                disabled={deleteMutation.isPending}
                              />
                            </>
                          )}
                        </ItemActions>
                      )}
                    </ItemHeader>
                    <ItemContent className="pt-0">
                      {isEditing ? (
                        <Textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onKeyPress={(e) => handleEditPress(e, comment.id)}
                          rows={3}
                          autoFocus
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap break-words">{comment.text}</p>
                      )}
                    </ItemContent>
                  </Item>
                </div>
              );
            })
          )}
        </div>
        <Sentinel ref={bottomRef} />
      </ScrollArea>
      <div className="p-1 pt-3 mt-auto">
        <Textarea
          value={newComment}
          classNames={{ iconRight: '-bottom-2' }}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={handlePress}
          placeholder={t('dialogs:addCommentPlaceholder')}
          iconRight={
            <Tooltip tooltip={<span className="text-sm">{t('dialogs:shiftEnterToSend')}</span>}>
              <Button
                variant="ghost"
                icon={<SendIcon />}
                className="size-6"
                aria-label={t('common:send')}
                onClick={handleCreate}
              />
            </Tooltip>
          }
          rows={3}
        />
      </div>
    </AwaitContainer>
  );
};
